declare global { interface Window { adsbygoogle?: Record<string, unknown>[] } }
export type AdPlacement = 'left' | 'right' | 'bottom';
export interface AdSettings { enabled: boolean; client: string; slots: Record<AdPlacement, string> }
export function adSettings(env: Record<string, string | boolean | undefined>): AdSettings {
  return { enabled: env.VITE_ADSENSE_ENABLED === 'true', client: String(env.VITE_ADSENSE_CLIENT ?? ''),
    slots: { left: String(env.VITE_ADSENSE_LEFT_SLOT ?? ''),
      right: String(env.VITE_ADSENSE_RIGHT_SLOT ?? ''), bottom: String(env.VITE_ADSENSE_BOTTOM_SLOT ?? '') } };
}
export function isAdConfigured(settings: AdSettings) { return settings.enabled && /^ca-pub-\d{16}$/.test(settings.client); }
export function initAds(settings = adSettings(import.meta.env)) {
  const slots = [...document.querySelectorAll<HTMLElement>('[data-ad-placement]')];
  // Empty manual units have no visible placeholder or reserved layout space.
  for (const el of slots) el.hidden = true;
  if (!isAdConfigured(settings)) return;
  const elements = slots.filter(el => /^\d+$/.test(settings.slots[el.dataset.adPlacement as AdPlacement] ?? ''));
  const sidebarViewport = window.matchMedia('(min-width: 1980px)');
  const portal = document.querySelector('.portal-grid');
  const syncLayout = () => {
    portal?.classList.toggle('has-side-ads', elements.some(el =>
      el.dataset.adPlacement !== 'bottom' && !el.hidden && sidebarViewport.matches));
  };
  const url = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + settings.client;
  // Production HTML includes the publisher's verification script in <head>.
  // Reuse it; development can still load it once from these settings.
  const existing = [...document.scripts].find(script => script.src === url);
  const script = existing ?? document.createElement('script');
  let scriptFailed = false;
  script.addEventListener('error', () => {
    scriptFailed = true;
    for (const el of elements) { el.dataset.adState = 'unavailable'; el.hidden = true; }
    syncLayout();
  }, { once: true });
  if (!existing) {
    script.async = true; script.src = url; script.crossOrigin = 'anonymous';
    document.head.append(script);
  }
  // The publisher script is useful without manual units. Never fabricate slot IDs.
  if (!elements.length) return;
  // Request once when a visible slot approaches the viewport. Never refresh on
  // replay/theme changes, and never initialize display:none sidebars on phones.
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement;
      if (!entry.isIntersecting || el.hidden || !el.offsetWidth || scriptFailed ||
          el.dataset.adState !== 'configured') continue;
      el.dataset.adState = 'requested'; observer.unobserve(el);
      const ad = document.createElement('ins'); ad.className = 'adsbygoogle';
      ad.style.display = 'block'; ad.style.width = '100%'; ad.style.height = '100%';
      ad.dataset.adClient = settings.client; ad.dataset.adSlot = settings.slots[el.dataset.adPlacement as AdPlacement];
      const status = new MutationObserver(() => {
        const value = ad.getAttribute('data-ad-status');
        if (value !== 'filled' && value !== 'unfilled') return;
        el.dataset.adState = value === 'filled' ? 'filled' : 'unavailable';
        if (value === 'unfilled') { el.hidden = true; syncLayout(); }
        status.disconnect();
      });
      status.observe(ad, { attributes: true, attributeFilter: ['data-ad-status'] });
      el.replaceChildren(ad); (window.adsbygoogle ??= []).push({});
    }
  }, { rootMargin: '200px 0px' });
  const updateVisibility = () => {
    for (const el of elements) {
      const eligible = !scriptFailed && el.dataset.adState !== 'unavailable' &&
        (el.dataset.adPlacement === 'bottom' || sidebarViewport.matches);
      el.hidden = !eligible;
      if (eligible && !el.dataset.adState) el.dataset.adState = 'configured';
      if (eligible && el.dataset.adState === 'configured') observer.observe(el);
      else observer.unobserve(el);
    }
    syncLayout();
  };
  sidebarViewport.addEventListener('change', updateVisibility);
  updateVisibility();
}
